import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Body1Strong,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  ProgressBar,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowSyncRegular,
  CheckmarkCircleRegular,
  DataUsageRegular,
  Dismiss24Regular,
  DocumentFlowchartRegular,
  DocumentLightningRegular,
  DocumentSearchRegular,
  PlayRegular,
} from '@fluentui/react-icons';

import getItemPathByItemType from '@modules/documents/helpers/getItemPathByItemType.helper';

import { CONTENT_AUTOMATION_STEP_DURATION_MS, CONTENT_AUTOMATION_STEPS } from '../constants';
import useContentAutomationStore from '../stores/useContentAutomationStore';
import type { TContentAutomationDataInconsistencyConfig, TContentAutomationMockConfig } from '../types';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '860px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    fontSize: '20px',
    lineHeight: 1,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalXL,
  },
  stepList: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
  },
  stepRow: {
    display: 'grid',
    gridTemplateColumns: '32px minmax(0, 1fr) 132px',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    minHeight: '112px',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  stepRowRunning: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  stepIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    fontSize: '18px',
  },
  stepIconRunning: {
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  stepIconCompleted: {
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  stepBody: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  statusText: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  progress: {
    width: '100%',
  },
  actionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  message: {
    minHeight: '18px',
    color: tokens.colorNeutralForeground3,
  },
  dataInconsistencyPrompt: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalXS,
  },
  dataInconsistencyHeading: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  dataInconsistencyLabel: {
    color: tokens.colorNeutralForeground2,
  },
  comparisonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalS,
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  comparisonOption: {
    minWidth: 0,
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr) auto',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  comparisonOptionHeader: {
    color: tokens.colorNeutralForeground2,
  },
  comparisonContent: {
    display: 'block',
    maxHeight: '168px',
    overflowY: 'auto',
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
  },
  runButton: {
    width: '100%',
  },
  summaryLink: {
    alignSelf: 'center',
    color: `${tokens.colorPaletteGreenForeground1} !important`,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'none',
  },
  summaryLinkDisabled: {
    color: `${tokens.colorNeutralForegroundDisabled} !important`,
  },
  closeButton: {
    marginLeft: tokens.spacingHorizontalS,
  },
});

const stepIcons = [<DocumentSearchRegular />, <DataUsageRegular />, <DocumentFlowchartRegular />];

const CONTENT_AUTOMATION_DATA_STEP_INDEX = CONTENT_AUTOMATION_STEPS.findIndex((step) => step.key === 'data');
const CONTENT_AUTOMATION_DATA_INCONSISTENCY_PROMPT_MS = CONTENT_AUTOMATION_STEP_DURATION_MS / 2;

type TDataInconsistencyResolutionState =
  | {
      runKey: string;
      status: 'waiting';
    }
  | {
      runKey: string;
      status: 'resolved';
      resolvedAt: number;
    };

type TProps = {
  config?: TContentAutomationMockConfig;
  currentDocumentId: string;
};

const getContentAutomationRunKey = (stepIndex: number, stepStartedAt: number) => `${stepIndex}:${stepStartedAt}`;

const getCompleteDataInconsistencyConfig = (
  config?: TContentAutomationDataInconsistencyConfig
): TContentAutomationDataInconsistencyConfig | undefined => {
  if (!config?.label.trim() || !config.originalContent.trim() || !config.libraryContent.trim()) return undefined;

  return config;
};

const getDataStepElapsedMs = (
  now: number,
  stepStartedAt: number,
  resolutionState?: TDataInconsistencyResolutionState
) => {
  if (resolutionState?.status === 'waiting') return CONTENT_AUTOMATION_DATA_INCONSISTENCY_PROMPT_MS;
  if (resolutionState?.status === 'resolved') {
    return CONTENT_AUTOMATION_DATA_INCONSISTENCY_PROMPT_MS + now - resolutionState.resolvedAt;
  }

  return Math.min(now - stepStartedAt, CONTENT_AUTOMATION_DATA_INCONSISTENCY_PROMPT_MS);
};

const ContentAutomationDialog = ({ config, currentDocumentId }: TProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const finishKeyRef = useRef<string>();
  const [now, setNow] = useState(Date.now());
  const [dataInconsistencyResolutionState, setDataInconsistencyResolutionState] =
    useState<TDataInconsistencyResolutionState>();
  const {
    isOpen,
    runningStepIndex,
    runMode,
    stepStartedAt,
    completedStepIndexes,
    close,
    open,
    startStep,
    completeStep,
  } = useContentAutomationStore();

  const dataInconsistencyConfig = getCompleteDataInconsistencyConfig(config?.dataInconsistency);
  const targetDocuments = useMemo(
    () => (config?.documents ?? []).map((document) => document.trim()),
    [config?.documents]
  );
  const currentConfiguredStepIndex = targetDocuments.findIndex((documentId) => documentId === currentDocumentId);
  const inferredCompletedStepIndexes = useMemo(
    () =>
      currentConfiguredStepIndex >= 0
        ? CONTENT_AUTOMATION_STEPS.map((_, stepIndex) => stepIndex).filter(
            (stepIndex) => stepIndex <= currentConfiguredStepIndex
          )
        : [],
    [currentConfiguredStepIndex]
  );
  const effectiveCompletedStepIndexes = useMemo(
    () => Array.from(new Set([...completedStepIndexes, ...inferredCompletedStepIndexes])),
    [completedStepIndexes, inferredCompletedStepIndexes]
  );
  const isRunning = runningStepIndex !== undefined && stepStartedAt !== undefined;
  const canRunAll = CONTENT_AUTOMATION_STEPS.every((_, stepIndex) => Boolean(targetDocuments[stepIndex]));
  const activeRunKey =
    runningStepIndex !== undefined && stepStartedAt !== undefined
      ? getContentAutomationRunKey(runningStepIndex, stepStartedAt)
      : undefined;
  const activeDataInconsistencyResolutionState =
    activeRunKey && dataInconsistencyResolutionState?.runKey === activeRunKey
      ? dataInconsistencyResolutionState
      : undefined;

  useEffect(() => {
    if (runningStepIndex === undefined || stepStartedAt === undefined) return undefined;

    const activeStepIndex = runningStepIndex;
    const activeStepStartedAt = stepStartedAt;

    const intervalId = window.setInterval(() => {
      const nextNow = Date.now();
      setNow(nextNow);

      const activeRunKey = getContentAutomationRunKey(activeStepIndex, activeStepStartedAt);
      const shouldPromptForDataInconsistency =
        activeStepIndex === CONTENT_AUTOMATION_DATA_STEP_INDEX && Boolean(dataInconsistencyConfig);
      const activeDataInconsistencyResolutionState =
        dataInconsistencyResolutionState?.runKey === activeRunKey ? dataInconsistencyResolutionState : undefined;
      const elapsedMs = shouldPromptForDataInconsistency
        ? getDataStepElapsedMs(nextNow, activeStepStartedAt, activeDataInconsistencyResolutionState)
        : nextNow - activeStepStartedAt;

      if (
        shouldPromptForDataInconsistency &&
        !activeDataInconsistencyResolutionState &&
        nextNow - activeStepStartedAt >= CONTENT_AUTOMATION_DATA_INCONSISTENCY_PROMPT_MS
      ) {
        setDataInconsistencyResolutionState({ runKey: activeRunKey, status: 'waiting' });
        return;
      }

      if (activeDataInconsistencyResolutionState?.status === 'waiting') return;

      if (elapsedMs < CONTENT_AUTOMATION_STEP_DURATION_MS) return;

      const finishKey = `${activeStepIndex}:${activeStepStartedAt}`;
      if (finishKeyRef.current === finishKey) return;
      finishKeyRef.current = finishKey;

      completeStep(activeStepIndex);

      const targetDocumentId = targetDocuments[activeStepIndex];
      if (targetDocumentId) {
        navigate(`/${getItemPathByItemType('DOCUMENT')}/${targetDocumentId}`, { replace: true });
      }

      if (runMode !== 'all') return;

      const nextStepIndex = activeStepIndex + 1;
      if (!CONTENT_AUTOMATION_STEPS[nextStepIndex] || !targetDocuments[nextStepIndex]) return;

      startStep(nextStepIndex, 'all');
    }, 120);

    return () => window.clearInterval(intervalId);
  }, [
    completeStep,
    dataInconsistencyConfig,
    dataInconsistencyResolutionState,
    navigate,
    runMode,
    runningStepIndex,
    startStep,
    stepStartedAt,
    targetDocuments,
  ]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isRunning) return;
    if (nextOpen) open();
    else close();
  };

  const getStepProgress = (stepIndex: number) => {
    if (runningStepIndex === stepIndex && stepStartedAt) {
      const elapsedMs =
        stepIndex === CONTENT_AUTOMATION_DATA_STEP_INDEX && dataInconsistencyConfig
          ? getDataStepElapsedMs(now, stepStartedAt, activeDataInconsistencyResolutionState)
          : now - stepStartedAt;

      return Math.max(0, Math.min(elapsedMs / CONTENT_AUTOMATION_STEP_DURATION_MS, 1));
    }

    return effectiveCompletedStepIndexes.includes(stepIndex) ? 1 : 0;
  };

  const handleResolveDataInconsistency = () => {
    if (!activeRunKey) return;

    const resolvedAt = Date.now();
    setNow(resolvedAt);
    setDataInconsistencyResolutionState({ runKey: activeRunKey, status: 'resolved', resolvedAt });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => handleOpenChange(data.open)}>
      <DialogSurface className={classes.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                className={classes.closeButton}
                disabled={isRunning}
                onClick={() => handleOpenChange(false)}
              />
            }
          >
            <div className={classes.titleRow}>
              <DocumentLightningRegular className={classes.titleIcon} />
              <span>Automate content</span>
            </div>
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            <div className={classes.stepList}>
              {CONTENT_AUTOMATION_STEPS.map((step, stepIndex) => {
                const progress = getStepProgress(stepIndex);
                const isStepRunning = runningStepIndex === stepIndex;
                const isCompleted = !isStepRunning && effectiveCompletedStepIndexes.includes(stepIndex);
                const targetDocumentId = targetDocuments[stepIndex];
                const isDataInconsistencyPromptVisible =
                  isStepRunning &&
                  stepIndex === CONTENT_AUTOMATION_DATA_STEP_INDEX &&
                  activeDataInconsistencyResolutionState?.status === 'waiting';
                const visibleDataInconsistencyConfig = isDataInconsistencyPromptVisible
                  ? dataInconsistencyConfig
                  : undefined;
                const messageIndex = Math.min(step.messages.length - 1, Math.floor(progress * step.messages.length));
                const message = visibleDataInconsistencyConfig
                  ? 'Inconsistency detected'
                  : isStepRunning
                    ? step.messages[messageIndex]
                    : isCompleted
                      ? 'Completed'
                      : targetDocumentId
                        ? 'Ready'
                        : 'Target document not configured';

                return (
                  <div
                    key={step.key}
                    className={mergeClasses(classes.stepRow, isStepRunning && classes.stepRowRunning)}
                  >
                    <div
                      className={mergeClasses(
                        classes.stepIcon,
                        isStepRunning && classes.stepIconRunning,
                        isCompleted && classes.stepIconCompleted
                      )}
                    >
                      {isCompleted ? (
                        <CheckmarkCircleRegular />
                      ) : isStepRunning ? (
                        <ArrowSyncRegular />
                      ) : (
                        stepIcons[stepIndex]
                      )}
                    </div>

                    <div className={classes.stepBody}>
                      <div className={classes.stepHeader}>
                        <Body1Strong>{step.title}</Body1Strong>
                        <Caption1 className={classes.statusText}>{Math.round(progress * 100)}%</Caption1>
                      </div>
                      <ProgressBar value={progress} thickness="medium" shape="rounded" className={classes.progress} />
                      <Caption1 className={classes.message}>{message}</Caption1>
                      {visibleDataInconsistencyConfig && (
                        <div className={classes.dataInconsistencyPrompt}>
                          <div className={classes.dataInconsistencyHeading}>
                            <Body1Strong>Resolve detected inconsistency</Body1Strong>
                            <Caption1 className={classes.dataInconsistencyLabel}>
                              {visibleDataInconsistencyConfig.label}
                            </Caption1>
                          </div>
                          <div className={classes.comparisonGrid}>
                            <div className={classes.comparisonOption}>
                              <Body1Strong className={classes.comparisonOptionHeader}>Original content</Body1Strong>
                              <Caption1 className={classes.comparisonContent}>
                                {visibleDataInconsistencyConfig.originalContent}
                              </Caption1>
                              <Button size="small" onClick={handleResolveDataInconsistency}>
                                Keep original
                              </Button>
                            </div>
                            <div className={classes.comparisonOption}>
                              <Body1Strong className={classes.comparisonOptionHeader}>Library content</Body1Strong>
                              <Caption1 className={classes.comparisonContent}>
                                {visibleDataInconsistencyConfig.libraryContent}
                              </Caption1>
                              <Button size="small" appearance="primary" onClick={handleResolveDataInconsistency}>
                                Align with library
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={classes.actionsColumn}>
                      <Button
                        size="small"
                        icon={<PlayRegular />}
                        className={classes.runButton}
                        disabled={isRunning || !targetDocumentId}
                        onClick={() => startStep(stepIndex, 'single')}
                      >
                        {isCompleted ? 'Run again' : 'Run'}
                      </Button>
                      <Link
                        className={mergeClasses(classes.summaryLink, !isCompleted && classes.summaryLinkDisabled)}
                        disabled={!isCompleted}
                        onClick={(event) => event.preventDefault()}
                      >
                        View summary
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>

          <DialogActions>
            <Button
              appearance="primary"
              icon={<PlayRegular />}
              disabled={isRunning || !canRunAll}
              onClick={() => startStep(0, 'all')}
            >
              Run all
            </Button>
            <Button appearance="secondary" disabled={isRunning} onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ContentAutomationDialog;
